import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import Voip, { Order, Token } from '@plugins/voip/voip.plugin';
import { AvatarOrderDataResponse, Client, UserDeviceRequest } from '@api/client';
import { PlatformService } from './platform.service';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { firstValueFrom } from 'rxjs';
import { NavigationStore } from '@services/navigation.store';
import { EventService } from '@services/event.service';
import { AvatarOrderService } from '@services/avatar-order.service';
import { CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { NOTIFICATION_TYPE } from '@constants/app.constants';

@Injectable({
  providedIn: 'root'
})
export class VoipNotificationsService {
  private readonly router: Router = inject(Router);
  private readonly client: Client = inject(Client);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly phoneInfoService: PhoneInfoService = inject(PhoneInfoService);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly eventService: EventService = inject(EventService);
  private readonly avatarOrderService: AvatarOrderService = inject(AvatarOrderService);

  async initializeVoipPushNotifications(): Promise<void> {
    if (!this.platformService.isDevice || !this.platformService.isIos) return;

    await Voip.addListener('voipPushRegistry', (async (token: Token): Promise<void> => {
      console.log(`VOIP token has been received ${token}`);

      const body = {
        token: token.token,
        type: 2,
        name: await this.phoneInfoService.getDeviceModelWithOS(),
      } as UserDeviceRequest;

      await firstValueFrom(this.client.userDevicePost(body));
    }));

    await Voip.addListener('handleOrder', (async (order: Order): Promise<void> => {
      const orderId: number = order.id;
      const nativeType: number | null = (typeof order.type === 'number') ? order.type : null;
      const orderType: number | null = this.normalizeIncomingOrderType(nativeType);
      await this.goToCall(orderId, orderType);
    }));

    await Voip.register();
    console.log('VoIP Push notification has been registered');
  }
  private async goToCall(orderId: number, orderType: number | null = null): Promise<void> {
    console.log(`Call has been received for ${orderId}`);
    const navigationCommands: (string | number)[] = await this.resolveIncomingOrderNavigationCommands(orderId, orderType);

    await this.ngZone.run(async (): Promise<void> => {
      this.eventService.closeModalsEvent.emit();
      if (this.shouldSaveCallBackUrl(this.router.url)) {
        this.navigationStore.setBackUrl(this.router.url);
      }
      await this.router.navigate(navigationCommands, {replaceUrl: true});
    });
  }

  private async resolveIncomingOrderNavigationCommands(orderId: number, orderType: number | null): Promise<(string | number)[]> {
    if (this.isAudioCallType(orderType)) {
      return ['/incoming-call', orderId];
    }
    if (typeof orderType === 'number') {
      return ['/avatar/order-details', orderId];
    }

    try {
      const response: AvatarOrderDataResponse = await this.avatarOrderService.getOrder(orderId, false);
      const resolvedOrderType: number | undefined = response?.data?.type;
      if (this.isAudioCallType(resolvedOrderType ?? null)) {
        return ['/incoming-call', orderId];
      }
    } catch (error) {
      console.error('VoIP notifications, failed to resolve order type', { orderId, error });
    }

    return ['/avatar/order-details', orderId];
  }

  private isAudioCallType(orderType: number | null): boolean {
    return orderType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  private normalizeIncomingOrderType(rawType: number | null): number | null {
    if (rawType === null) return null;
    if (rawType < 0) return null;
    if (rawType === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL) return rawType;
    if (rawType === NOTIFICATION_TYPE.CALL) return CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
    return rawType;
  }

  private shouldSaveCallBackUrl(url: string): boolean {
    const normalizedUrl: string = url.toLowerCase();
    return !normalizedUrl.includes('/incoming-call')
      && !normalizedUrl.includes('/audio-call')
      && !normalizedUrl.includes('/video-call');
  }
}
